import { TYPES, PRICE_TYPES, PRICE_SOURCES } from '@/constants/delivery-option';

const ISO_8601_TIME_DELIMITER = 'T';
const UNSET_CACHE_VALUE = undefined;

export default class DeliveryOption {
  constructor(apiOption, destinationToday) {
    this.apiOption = apiOption;
    this.destinationToday = destinationToday;

    this.isAvailable = apiOption.isAvailable;
    this.type = apiOption.type;
    this.price = apiOption.price;
    this.priceLabel = apiOption.priceLabel;
    this.priceType = apiOption.priceType;
    this.priceSource = apiOption.priceSource;
    this.deliveryDayIndex = apiOption.details.deliveryDayIndex;
    this.date = apiOption.details.date;
    this.label = apiOption.details.label;
    this.orderLabel = apiOption.details.orderLabel;
    this.dayOfWeek = apiOption.details.dayOfWeek;
    this.alternativeUrl = apiOption.alternativeUrl;
    this._initializePrivateCacheProperties();

    if (this.isFlex()) {
      this.flexCalendarHeaderText = apiOption.details.calendarHeaderText;
      this.flexCalendarSubheaderText = apiOption.details.calendarSubheaderText;
      this.flexBackupFeeLabel = apiOption.details.backupFeeLabel;
    }

    this.value = this.isFlex()
      ? apiOption.details.flexId
      : this.date.split(ISO_8601_TIME_DELIMITER)[0];
  }

  /**
   * Is this a Flex option?
   *
   * @returns boolean
   */
  isFlex() {
    if (this._isFlexCache !== UNSET_CACHE_VALUE) {
      return this._isFlexCache;
    }

    return this._isFlexCache = this.type === TYPES.FLEX;
  }

  /**
   * Is this a Date option?
   *
   * @returns boolean
   */
  isDate() {
    if (this._isDateCache !== UNSET_CACHE_VALUE) {
      return this._isDateCache;
    }

    return this._isDateCache = this.type === TYPES.DATE;
  }

  /**
   * Is this today's delivery date option?
   *
   * @returns boolean
   */
  isToday() {
    if (this._isTodayCache !== UNSET_CACHE_VALUE) {
      return this._isTodayCache;
    }

    return this._isTodayCache = this.isDate() && (this.date === this.destinationToday);
  }

  /**
   * Is this the first _possible_ delivery date option according to the calendar
   * (not according to the date range given)?
   *
   * This is what we call "Same Day" internally, which is today's calendar date
   * until cutoff and tomorrow's calendar date after cutoff even if tomorrow's
   * date option is unavailable
   *
   * @returns boolean
   */
  isFirstPossibleDeliveryDate() {
    if (this._isFirstPossibleDeliveryDateCache !== UNSET_CACHE_VALUE) {
      return this._isFirstPossibleDeliveryDateCache;
    }

    return this._isFirstPossibleDeliveryDateCache = this.isDate() && (this.deliveryDayIndex === 0);
  }

  /**
   * Does this option have a relative delivery day index delivery fee?
   *
   * @returns boolean
   */
  hasRelativeFee() {
    if (this._hasRelativeFeeCache !== UNSET_CACHE_VALUE) {
      return this._hasRelativeFeeCache;
    }

    return this._hasRelativeFeeCache = this.priceType === PRICE_TYPES.DELIVERY_DAY_INDEX;
  }

  /**
   * Does this option's fee source from the queried zip code?
   *
   * @returns boolean
   */
  hasFeeFromZipCode() {
    if (this._hasFeeFromZipCodeCache !== UNSET_CACHE_VALUE) {
      return this._hasFeeFromZipCodeCache;
    }

    return this._hasFeeFromZipCodeCache = this.priceSource === PRICE_SOURCES.ZIP_CODE;
  }

  /**
   * Does this option have a relative delivery fee that is sourced from
   * the zip code?
   *
   * @returns boolean
   */
  hasRelativeFeeFromZipCode() {
    if (this._hasRelativeFeeFromZipCodeCache !== UNSET_CACHE_VALUE) {
      return this._hasRelativeFeeFromZipCodeCache;
    }

    return this._hasRelativeFeeFromZipCodeCache = this.hasRelativeFee() && this.hasFeeFromZipCode();
  }

  /**
   * Called only during initialization to explicitly set all
   * "private" properties used for caching method values
   * Technically, we could just not initialize these and they would
   * implicitly be set to undefined, but this feels like a slightly
   * safer guard against quirky browser/type behavior
   *
   * @returns void
   */
  _initializePrivateCacheProperties() {
    this._isFlexCache
      = this._isDateCache
      = this._isTodayCache
      = this._isFirstPossibleDeliveryDateCache
      = this._hasRelativeFeeCache
      = this._hasFeeFromZipCodeCache
      = this._hasRelativeFeeFromZipCodeCache
      = UNSET_CACHE_VALUE;
  }
}